/**
 * CSV Shipment parsing and validation utilities.
 * Columns: S.No., First Name, Last Name, Contact No., Alt. Contact No., Email Id,
 * Address Line 1, Address Line 2, Address Country, Address State, Address City,
 * Address Postcode, Size If Any (Jacket/T-shirt), Order Notes
 */


export type ShipmentType = "domestic" | "international";

export interface CsvRow {
  rowIndex: number;
  sNo: string;
  code: string;
  firstName: string;
  lastName: string;
  contactNo: string;
  altContactNo: string;
  emailId: string;
  addressLine1: string;
  addressLine2: string;
  addressCountry: string;
  addressState: string;
  addressCity: string;
  addressPostcode: string;
  sizeIfAny: string;
  orderNotes: string;
}

export interface CsvValidationError {
  row: number;
  message: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Robust CSV parser that supports:
 * - quoted fields with commas
 * - quoted fields with embedded newlines
 * - escaped quotes via double quotes ("")
 *
 * This follows the common RFC4180 behavior closely enough for our uploads.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  const pushCell = () => {
    row.push(cell.trim());
    cell = "";
  };

  const pushRow = () => {
    // Avoid pushing a trailing completely-empty row (common with ending newline)
    if (row.length === 1 && row[0] === "") {
      row = [];
      return;
    }
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];

    if (c === '"') {
      // Escaped quote inside quoted field => ""
      if (inQuotes && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (c === "," && !inQuotes) {
      pushCell();
      continue;
    }

    if ((c === "\n" || c === "\r") && !inQuotes) {
      // Handle CRLF
      if (c === "\r" && text[i + 1] === "\n") i++;
      pushCell();
      pushRow();
      continue;
    }

    cell += c;
  }

  // Flush last cell/row
  pushCell();
  if (row.length > 0) pushRow();

  return rows;
}

function isRowEmpty(cells: string[]): boolean {
  return cells.every((c) => !c || c.trim() === "");
}

/** Expected CSV headers (order does not matter; lookup is by name) */
const CSV_HEADERS: {
  key: keyof Omit<CsvRow, "rowIndex">;
  aliases: string[];
}[] = [
  { key: "sNo", aliases: ["S.No.", "S.No", "S No", "SNo"] },
  { key: "code", aliases: ["Code"] },
  { key: "firstName", aliases: ["First Name", "FirstName", "First name"] },
  { key: "lastName", aliases: ["Last Name", "LastName", "Last name"] },
  { key: "contactNo", aliases: ["Contact No.", "Contact No", "Contact No"] },
  {
    key: "altContactNo",
    aliases: ["Alt. Contact No.", "Alt Contact No.", "Alt. Contact No"],
  },
  { key: "emailId", aliases: ["Email Id", "Email ID", "EmailId", "Email"] },
  {
    key: "addressLine1",
    aliases: ["Address Line 1", "Address Line1", "Address 1"],
  },
  {
    key: "addressLine2",
    aliases: ["Address Line 2", "Address Line2", "Address 2"],
  },
  { key: "addressCountry", aliases: ["Address Country", "Country"] },
  { key: "addressState", aliases: ["Address State", "State"] },
  { key: "addressCity", aliases: ["Address City", "City"] },
  { key: "addressPostcode", aliases: ["Address Postcode", "Postcode"] },
  {
    key: "sizeIfAny",
    aliases: ["Size If Any (Jacket/T-shirt)", "Size If Any", "Size"],
  },
  {
    key: "orderNotes",
    aliases: ["Order Notes", "Order Note", "Notes"],
  },
];

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, " ");
}

function buildHeaderIndexMap(headerCells: string[]): Record<string, number> {
  const map: Record<string, number> = {};

  for (const { key, aliases } of CSV_HEADERS) {
    const normalizedAliases = new Set(aliases.map(normalizeHeader));
    const idx = headerCells.findIndex((h) =>
      normalizedAliases.has(normalizeHeader(h)),
    );
    if (idx >= 0) {
      map[key] = idx;
    }
  }
  return map;
}

function getColByKey(
  cells: string[],
  headerMap: Record<string, number>,
  key: keyof Omit<CsvRow, "rowIndex">,
): string {
  const idx = headerMap[key];
  if (idx == null || idx < 0) return "";
  return cells[idx]?.trim() ?? "";
}

export function parseCsvContent(text: string): CsvRow[] {
  const records = parseCsv(text);
  const nonEmptyRecords = records.filter((cells) => !isRowEmpty(cells));
  if (nonEmptyRecords.length < 2) return [];

  const headerCells = nonEmptyRecords[0];
  const headerMap = buildHeaderIndexMap(headerCells);
  const rows: CsvRow[] = [];

  for (let i = 1; i < nonEmptyRecords.length; i++) {
    const cells = nonEmptyRecords[i];

    const g = (k: keyof Omit<CsvRow, "rowIndex">) =>
      getColByKey(cells, headerMap, k);

    rows.push({
      rowIndex: i + 1,
      sNo: g("sNo"),
      code: g("code"),
      firstName: g("firstName"),
      lastName: g("lastName"),
      contactNo: g("contactNo"),
      altContactNo: g("altContactNo"),
      emailId: g("emailId"),
      addressLine1: g("addressLine1"),
      addressLine2: g("addressLine2"),
      addressCountry: g("addressCountry"),
      addressState: g("addressState"),
      addressCity: g("addressCity"),
      addressPostcode: g("addressPostcode"),
      sizeIfAny: g("sizeIfAny"),
      orderNotes: g("orderNotes"),
    });
  }

  return rows;
}

export function validateCsvRows(
  rows: CsvRow[],
  shipmentType: ShipmentType,
): CsvValidationError[] {
  const errors: CsvValidationError[] = [];

  if (rows.length === 0) {
    errors.push({ row: 0, message: "No data rows found in CSV" });
    return errors;
  }

  for (const row of rows) {
    if (!row.firstName?.trim()) {
      errors.push({ row: row.rowIndex, message: "First Name is required" });
    }
    if (!row.emailId?.trim()) {
      errors.push({ row: row.rowIndex, message: "Email Id is required" });
    } else if (!EMAIL_REGEX.test(row.emailId)) {
      errors.push({ row: row.rowIndex, message: "Invalid email format" });
    }
    if (!row.contactNo?.trim()) {
      errors.push({ row: row.rowIndex, message: "Contact No. is required" });
    } else if (shipmentType === "domestic") {
      const digits = row.contactNo.replace(/\D/g, "");
      if (
        digits.length === 10 ||
        (digits.length === 11 && digits.startsWith("0"))
      ) {
        // valid
      } else {
        errors.push({
          row: row.rowIndex,
          message:
            "Contact No. must be 10 digits or 11 digits (starting with 0) for domestic",
        });
      }
    }
    if (!row.addressCountry?.trim()) {
      errors.push({
        row: row.rowIndex,
        message: "Address Country is required",
      });
    }
    if (!row.addressState?.trim()) {
      errors.push({ row: row.rowIndex, message: "Address State is required" });
    }
    if (!row.addressCity?.trim()) {
      errors.push({ row: row.rowIndex, message: "Address City is required" });
    }
    if (!row.addressPostcode?.trim()) {
      errors.push({
        row: row.rowIndex,
        message: "Address Postcode is required",
      });
    } else if (shipmentType === "domestic") {
      const postcode = row.addressPostcode.replace(/\D/g, "");
      if (postcode.length !== 6) {
        errors.push({
          row: row.rowIndex,
          message: "Postcode must be exactly 6 digits for domestic",
        });
      }
    }
    if (!row.addressLine1?.trim()) {
      errors.push({ row: row.rowIndex, message: "Address Line 1 is required" });
    }
  }

  return errors;
}

export function removeEmptyRowsFromCsvText(text: string): string {
  const records = parseCsv(text);
  const nonEmptyRecords = records.filter((cells) => !isRowEmpty(cells));
  if (nonEmptyRecords.length < 2) return text;

  const escapeCell = (value: string) => {
    const needsQuotes =
      value.includes(",") ||
      value.includes('"') ||
      value.includes("\n") ||
      value.includes("\r");
    if (!needsQuotes) return value;
    return `"${value.replace(/"/g, '""')}"`;
  };

  return nonEmptyRecords
    .map((row) => row.map((cell) => escapeCell(cell ?? "")).join(","))
    .join("\n");
}

export function createCsvFileFromText(text: string, filename: string): File {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  return new File([blob], filename, { type: "text/csv" });
}

/** Validates email format */
export function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

/** Validates domestic phone: 10 digits or 11 digits (starting with 0) */
export function validatePhoneDomestic(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("0"))
  );
}

/** Validates domestic postcode: exactly 6 digits */
export function validatePostcodeDomestic(pincode: string): boolean {
  const digits = pincode.replace(/\D/g, "");
  return digits.length === 6;
}
